import { ErrorMessage, Field, Form, Formik } from "formik"; // Importing necessary libraries and components
import { observer } from "mobx-react"; // Importing MobX observer for state management
import moment from "moment";
import React from "react"; // Importing React
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import * as Yup from "yup"; // Importing Yup for validation schema
import { SMRTabEnum } from "../../../../constants/enums/smr-tab-enum";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader"; // Importing form loader component
import { formatMessage } from "../../../../translations/format-message";
import RoutesConstants from "../../../routes.constant";
import CancelReservationPopupHOC from "../hoc/cancel-reservation-popup-hoc"; // Importing HOC for cancel reservation popup
import RebookReservationPopupHOC from "../hoc/rebook-reservation-popup-hoc";
import RebookReservationComponent from "../modal-components/rebook-reservation-component";
import CancelReservationFormComponent from "./cancel-reservation-form-component"; // Importing cancel reservation form component

interface IProps {
  id: number; // Reservation ID
  initialValues: any; // Initial form values
  isLoading: boolean; // Loading state
  staffId: number; // Staff ID
  storeId: number; // Store ID
  submitHandler: (data: any) => void; // Submit handler function
  cancelReservationPopup: boolean; // Cancel reservation popup state
  rebookReservationPopup: boolean; // Rebook reservation popup state
  cancelReservationToggleHandler: () => void; // Toggle handler for cancel reservation popup
  rebookReservationToggleHandler: () => void; // Toggle handler for rebook reservation popup
  cancelReservationSubmitHandler: () => void; // Submit handler for cancel reservation
  setSelectedAppointmentIdForPayment: (id: number) => void; // Set selected appointment ID for payment
  viewOnly: boolean; // View-only mode flag
  // setActiveTab: (tab: string) => void; // Set active tab for SMR function 
}

const ReservationMemoForm = (props: IProps) => {
  const history = useHistory();
  const validationSchema = Yup.object({
    ReservationMemo: Yup.string().max(
      256,
      formatMessage("max_256_characters") // Validation for memo field
    ),
  });

  const PushToSales = (Id: number, openAccounting: boolean) => {
    // props.setActiveTab(SMRTabEnum.Sales);
    props.setSelectedAppointmentIdForPayment(Id); // Set selected appointment ID for payment
    let locationState = {
        appointmentId: Id,
        customerId: props.initialValues?.CustomerId,
        storeId: props.initialValues.StoreId,
        from: RoutesConstants.ReservationInformation,
        openAccounting,
        Tab: SMRTabEnum.Sales,
      };
    localStorage.setItem("lizera_smr_info", JSON.stringify(locationState));
    history.push({
      pathname: RoutesConstants.SalesMedicalRecords, // Navigate to customer management
      state: {
        appointmentId: Id,
        customerId: props.initialValues?.CustomerId,
        storeId: props.initialValues.StoreId,
        from: RoutesConstants.ReservationInformation,
        openAccounting,
        Tab: SMRTabEnum.Sales,
      }, // Pass customer ID
    });
  };

  const PushToMR = (Id: number, openAccounting: boolean) => {
    // props.setSelectedAppointmentIdForPayment(Id); // Set selected appointment ID for payment
    // props.setActiveTab(SMRTabEnum.Medical); // Set active tab for SMR function
    history.push({
      pathname: RoutesConstants.SalesMedicalRecords, // Navigate to customer management
      state: {
        appointmentId: Id,
        customerId: props.initialValues?.CustomerId,
        storeId: props.storeId,
        from: RoutesConstants.ReservationInformation,
        openAccounting,
        Tab: SMRTabEnum.Medical,
      }, // Pass customer ID
    });
  };

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    props.submitHandler(values); // Call submit handler with form values
    setSubmitting(false); // Stop form submission state
  };

  const handleCancelReservation = () => {
    props.cancelReservationToggleHandler();
  };

  const handleCloseCancelReservationPopup = () => {
    props.cancelReservationToggleHandler();
  };

  const handleCloseRebookReservationPopup = () => {
    props.rebookReservationToggleHandler();
  };

  return (
    <>
      {props.cancelReservationPopup && (
        <CancelReservationFormComponent
          submitCancelHandler={props.cancelReservationSubmitHandler} // Submit handler for cancel reservation
          onModalClosed={handleCloseCancelReservationPopup} // Handler for closing modal
          isLoading={props.isLoading} // Loading state
        />
      )}

      {props.rebookReservationPopup && (
        <RebookReservationComponent
          appointment={{
            ...props.initialValues,
            Date: moment().format("YYYY/MM/DD"),
            Time: moment().format("HH:mm"),
          }} // Appointment details
          onModalClosed={handleCloseRebookReservationPopup} // Handler for closing modal
          isLoading={props.isLoading} // Loading state
        />
      )}

      <div>
        <Formik
          initialValues={props.initialValues} // Initial form values
          enableReinitialize // Reinitialize form when initialValues change
          validationSchema={validationSchema} // Validation schema for form
          onSubmit={handleSubmit} // Form submit handler
        >
          {({ isSubmitting }) => (
            <Form>
              {props.isLoading && <FormLoader loading={props.isLoading} />}{" "}
              {/* Show loader if loading */}
              <div className="mb-3">
                <label
                  htmlFor="memo"
                  className="block text-gray-700 font-medium"
                >
                  <FormattedMessage id="memo" />
                </label>
                <Field
                  as="textarea"
                  id="memo"
                  name="ReservationMemo" // Field for reservation memo
                  rows="4"
                  disabled={
                    props.viewOnly
                      ? props.viewOnly
                      : props.initialValues?.AppointmentStatus == 1 ||
                        props.initialValues?.AppointmentStatus == 2 // Disable field based on appointment status
                      ? true
                      : false
                  }
                  className="form-textarea"
                />
                <ErrorMessage
                  name="ReservationMemo" // Error message for memo field
                  component="div"
                  className="text-danger small mt-1"
                />
              </div>
              <div className="mb-3">
                <label className="flex items-center pb-4">
                  <Field
                    type="checkbox"
                    name="Reception" // Checkbox for reception
                    disabled={
                      props.viewOnly
                        ? props.viewOnly
                        : props.initialValues?.AppointmentStatus == 1 ||
                          props.initialValues?.AppointmentStatus == 2 // Disable checkbox based on appointment status
                        ? true
                        : false
                    }
                    className="h-5 w-5 border border-gray-300 rounded"
                  />
                  <span className="ms-2 text-gray-700">
                    {formatMessage("reception")}
                  </span>
                </label>
              </div>
              {!props.viewOnly && ( // Show buttons only if not in view-only mode
                <div className="d-flex gap-2 align-items-center justify-content-center">
                  {props.initialValues?.AppointmentStatus == 2 && (
                    <React.Fragment>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                          PushToSales(props.id, false);
                        }}
                      >
                        {formatMessage("sales_medical")}{" "}
                        {/* Button for sales/medical */}
                      </button>
                      {/* Button for rebooking */}
                    </React.Fragment>
                  )}
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => {
                          handleCloseRebookReservationPopup(); // Close rebook reservation popup
                        }}
                      >
                        {formatMessage("rebook")}
                      </button>{" "}
                  {((props.initialValues?.AppointmentStatus == 0 )|| (props.initialValues?.AppointmentStatus == 3 )) && (
                    <React.Fragment>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                          PushToSales(props.id, true);
                        }}
                      >
                        {formatMessage("sales_medical")}
                      </button>{" "}
                      {/* Button for payment */}
                    </React.Fragment>
                  )}

                  {(props.initialValues?.AppointmentStatus == 1 ||
                    props.initialValues?.AppointmentStatus == 2) == false && (
                    <button
                      className="btn btn-secondary"
                      type="submit"
                      disabled={isSubmitting} // Disable button while submitting
                    >
                      {formatMessage("button_update")}{" "}
                      {/* Button for updating reservation */}
                    </button>
                  )}

                  {(props.initialValues?.AppointmentStatus == 0 || props.initialValues?.AppointmentStatus == 3) && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        handleCancelReservation(); // Toggle cancel reservation popup
                      }}
                    >
                      {formatMessage("cancel_reservation")}
                    </button>
                  )}
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default CancelReservationPopupHOC(
  RebookReservationPopupHOC(observer(ReservationMemoForm))
); // Exporting component wrapped with HOC and observer
